import request from '@/core/request'
import { ref, onMounted } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useRoute } from 'vue-router'
import { useToast } from '@/ui-runtime/core/composable/use-toast'
import dayjs from 'dayjs'
import { useDialogs } from '@/ui-runtime/core/composable/use-dialogs'

export type CheckinDayImage =
  | string
  | {
      key?: string
      unchecked?: string
      checked?: string
      layout?: Record<string, unknown>
    }

export type CheckinUiMeta = {
  key?: string
  type?: string
  images?: {
    checkinDialogBg?: string
    checkinEntry?: string
    checkinDays?: CheckinDayImage[]
    checkinGot?: string
    checkinGet?: string
    checkinIconChecked?: string
  }
  layout?: {
    wrapper?: {
      tip?: Record<string, unknown>
      checkinContent?: Record<string, unknown>
      row?: Record<string, unknown>
      button?: Record<string, unknown>
      closeButton?: Record<string, unknown>
      checkedIconPositions?: Record<string, unknown>[]
    }
  }
  config?: {
    configApi?: string
    signinApi?: string
    componentId?: number | string
    checkinDialogName?: string
    tipText?: string
    dayCount?: number
    columns?: number
    rowCounts?: number[]
    autoOpen?: boolean
    checkedImagePattern?: string
    uncheckedImagePattern?: string
  }
}

export const defaultConfig = {
  key: 'ui-checkin1', // 每个ui组件必须有
  type: 'ui-checkin1', //
  images: {
    checkinDialogBg: 'checkin-dialog-bg',
    checkinEntry: 'checkin-icon-entry',
    checkinDays: ['day1', 'day2', 'day3', 'day4', 'day5', 'day6', 'day7'],
    checkinGot: 'checkin-btn-got',
    checkinGet: 'checkin-btn-get',
    checkinIconChecked: 'checkin-icon-checked',
  }, // 每个组件的配置都统一放到images中管理
  layout: {
    wrapper: {
      tip: {
        f: '14 #CE6C3D 400',
        pc: 'x',
        pb: '94',
      },
      checkinContent: {
        wh: '277 230',
        pc: 'x',
        pt: '156',
      },
      row: {},
      button: {
        wh: '88 34',
        pc: 'x',
        pb: '33',
      },
      closeButton: {
        wh: '30',
        pl: '307',
        pt: '85',
      },
      checkedIconPositions: [
        { pt: '-8', pr: '-8' },
        { pt: '-4', pr: '-2' },
      ],
    },
  },
  config: {
    configApi: '/api/activities/general/checkin/detail',
    signinApi: '/api/activities/general/checkin',
    componentId: 6,
    checkinDialogName: 'checkin',
    tipText: '您已领第{progress}天奖励，继续加油哦～',
    dayCount: 7,
    columns: 3,
    // 保持历史活动原有的 3 + 4 排列；非 7 天配置会按 columns 自动分行。
    rowCounts: [3, 4],
    autoOpen: true,
    checkedImagePattern: 'checkin-{day}-Checked',
    uncheckedImagePattern: 'checkin-{day}-Check',
  }, //每个ui组件的一些配置字段相关的东西
}

export const useCheckin = (uiMeta: CheckinUiMeta = defaultConfig) => {
  const { config } = uiMeta
  const route = useRoute()
  const { $dialogs } = useDialogs()
  const activity_code = route.meta?.activity_code
  const componentId = config?.componentId

  const progress = ref(0)
  const isSignToday = ref(false)
  const { $toast } = useToast()
  const { data: configData, refetch: refetchConfigData } = useQuery({
    queryKey: ['checkinConfigData', activity_code, componentId, config?.configApi],
    queryFn: async () => {
      const res = await request.get(config?.configApi || defaultConfig.config.configApi, {
        activity_code,
        component_id: componentId,
      })
      if (res.status !== 0) {
        $toast.show((res as any).msg || '获取签到配置失败')
        return
      }

      progress.value = res.data.finish_checkin_days
      isSignToday.value = res.data.current_status === 1
      return res
    },
  })

  const open = () => {
    const dialogName = config?.checkinDialogName || defaultConfig.config.checkinDialogName
    $dialogs[dialogName]?.open({
      data: {
        configData: configData.value,
      },
    })
  }

  const closeCheckin = () => {
    const dialogName = config?.checkinDialogName || defaultConfig.config.checkinDialogName
    $dialogs[dialogName]?.close()
  }

  // 处理自动弹出逻辑，每天自动弹出一次，以日期和路由作为key标识
  onMounted(() => {
    if (config?.autoOpen === false) {
      return
    }
    const key = `${activity_code}-${componentId}-${dayjs().format('YYYY-MM-DD')}`
    const stored = localStorage.getItem(key)
    if (stored === 'true') {
      return
    }
    open()
    localStorage.setItem(key, 'true')
  })

  const signin = async () => {
    if (isSignToday.value) {
      return false
    }
    const res = await request.post(config?.signinApi || defaultConfig.config.signinApi, {
      activity_code,
      component_id: componentId,
    })
    if (res.status !== 0) {
      $toast.show((res as any).msg || '签到失败')
      return false
    }

    await refetchConfigData()
    return true
  }

  return {
    signin,
    progress,
    isSignToday,
    open,
    closeCheckin,
    refetchConfigData,
  }
}
